// Servicio API con Axios
// Los tokens JWT viajan en cookies httpOnly (access_token, refresh_token).
// El browser los envía automáticamente con withCredentials: true.
// Ya NO se leen/escriben tokens desde localStorage — sólo se guarda user data y permisos.
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '@/config/api';

class ApiService {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private refreshQueue: Array<(retry: boolean) => void> = [];

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true, // Envía cookies httpOnly automáticamente
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Endpoints que no deben activar refresh automático.
    // - /api/auth/refresh: evita loop directo si el propio refresh devuelve 401
    // - /api/auth/login: el login crea tokens nuevos, no necesita refresh previo
    // NOTA: /api/auth/me NO está excluida — si el access_token expiró al arrancar la app,
    // el interceptor usa el refresh_token para restaurar la sesión transparentemente.
    // El flag _retry en el interceptor previene cualquier loop posterior.
    const NO_REFRESH_URLS = ['/api/auth/refresh', '/api/auth/login'];

    // Rutas públicas (sin auth) — ver bloque "Public routes" en App.tsx. AuthProvider
    // envuelve toda la app, así que /api/auth/me se dispara al montar en CUALQUIER
    // ruta, incluidas estas. Si el visitante/empleado no tiene cookie de sesión, el
    // refresh también falla (401) — sin esta exclusión, el catch de abajo lo saca a
    // la fuerza de la página pública que vino a usar y lo manda a /login.
    const PUBLIC_PATH_PREFIXES = [
      '/login', '/confirm-visit/', '/reservas/', '/visita/invitacion/',
      '/enrolamiento-facial/', '/cambiar-pin/', '/pase-acceso/', '/qr-empleado/',
      '/activar-cuenta/', '/reset-password',
    ];
    const isPublicPath = () => PUBLIC_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p));

    // Response interceptor — manejo de 401 con refresh automático por cookie
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<any>) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        const isNoRefreshUrl = NO_REFRESH_URLS.some((u) => originalRequest.url?.includes(u));

        // Si el error es 401, intentar refresh (el refresh_token viaja en cookie automáticamente).
        // _retry garantiza que no se reintenta más de una vez por request (no hay loops).
        if (error.response?.status === 401 && !originalRequest._retry && !isNoRefreshUrl) {
          originalRequest._retry = true;

          if (this.isRefreshing) {
            // Otro refresh en curso — encolar y esperar
            return new Promise((resolve, reject) => {
              this.refreshQueue.push((success) => {
                if (success) resolve(this.axiosInstance(originalRequest));
                else reject(error);
              });
            });
          }

          this.isRefreshing = true;

          try {
            // El refresh_token viaja en cookie httpOnly — no hace falta enviarlo en body
            await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, { withCredentials: true });

            // Notificar a todos los encolados que el refresh fue exitoso
            this.refreshQueue.forEach((cb) => cb(true));
            this.refreshQueue = [];

            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh falló — limpiar sesión y redirigir a login
            this.refreshQueue.forEach((cb) => cb(false));
            this.refreshQueue = [];

            // Solo limpiar y redirigir si había sesión activa y no estamos en una
            // página pública (el visitante/empleado no tiene por qué tener sesión ahí).
            if (!isPublicPath()) {
              localStorage.removeItem('permissions');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Despachar evento para errores de plan (403 con código específico)
        if (error.response?.status === 403) {
          const planCode = error.response?.data?.code;
          if (
            planCode === 'MODULE_NOT_AVAILABLE' ||
            planCode === 'QUOTA_EXCEEDED' ||
            planCode === 'CREDENTIAL_TYPE_NOT_AVAILABLE'
          ) {
            window.dispatchEvent(
              new CustomEvent('plan:module-blocked', { detail: error.response.data })
            );
          }
        }

        // Extraer información de error del backend
        const backendData = error.response?.data;
        const backendMessage = backendData?.message;
        const backendErrorCode = backendData?.error;
        const backendDetails = backendData?.details;

        const enhancedError = new Error(
          backendMessage || backendErrorCode || error.message || 'Error en la solicitud'
        );

        (enhancedError as any).response = error.response;
        (enhancedError as any).status = error.response?.status;
        (enhancedError as any).error = backendErrorCode;
        (enhancedError as any).details = backendDetails;
        (enhancedError as any).data = backendData;

        return Promise.reject(enhancedError);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.delete(url, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.patch(url, data, config);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
